import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { KeyRound } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const UpdatePassword = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isRecovery, setIsRecovery] = useState(false);
  const [isCheckingSession, setIsCheckingSession] = useState(true);

  useEffect(() => {
    // Check URL params first for recovery type
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const searchParams = new URLSearchParams(window.location.search);
    const type = hashParams.get('type') || searchParams.get('type');
    
    if (type === 'recovery') {
      console.log('UpdatePassword: detected recovery type in URL');
      setIsRecovery(true);
    }

    // Listen for PASSWORD_RECOVERY auth event
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('UpdatePassword: auth state change', event);
      if (event === 'PASSWORD_RECOVERY') {
        console.log('UpdatePassword: PASSWORD_RECOVERY event received');
        setIsRecovery(true);
        setIsCheckingSession(false);
      } else if (event === 'SIGNED_IN' && session) {
        // If we have a session and got here via navigation, assume recovery
        setIsRecovery(true);
        setIsCheckingSession(false);
      }
    });

    // Also check existing session - if user landed here with a valid session, allow password update
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        console.log('UpdatePassword: found existing session, enabling password update');
        setIsRecovery(true);
      }
      setIsCheckingSession(false);
    });

    // Clean URL after processing
    try {
      window.history.replaceState({}, document.title, window.location.pathname);
    } catch {}

    return () => subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const passwordSchema = z.string().min(8, 'Password must be at least 8 characters');
    const parsed = passwordSchema.safeParse(password);
    if (!parsed.success) {
      setIsLoading(false);
      toast({
        title: 'Invalid password',
        description: parsed.error.errors[0].message,
        variant: 'destructive',
      });
      return;
    }

    if (password !== confirmPassword) {
      setIsLoading(false);
      toast({
        title: 'Passwords do not match',
        description: 'Please make sure both passwords are the same',
        variant: 'destructive',
      });
      return;
    }

    try {
      const { error } = await supabase.auth.updateUser({
        password: password,
      });

      if (error) throw error;

      // Update has_password flag
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase
          .from('users')
          .update({ has_password: true })
          .eq('id', user.id);
      }

      toast({
        title: 'Password updated',
        description: 'Your password has been updated successfully',
      });

      // Navigate to map after success
      setTimeout(() => {
        navigate('/map');
      }, 1000);
    } catch (error: any) {
      console.error('Password update error:', error);
      
      // Check for specific password-related errors from Supabase Auth
      let errorMessage = 'Failed to update password. Please try again.';
      if (error?.message) {
        if (error.message.toLowerCase().includes('same') || 
            error.message.toLowerCase().includes('different')) {
          errorMessage = 'New password must be different from your current password.';
        } else if (error.message.toLowerCase().includes('weak') || 
                   error.message.toLowerCase().includes('short')) {
          errorMessage = 'Password is too weak. Please choose a stronger password.';
        } else {
          errorMessage = error.message;
        }
      }
      
      toast({
        title: 'Update failed',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isCheckingSession) {
    return (
      <div className="min-h-screen bg-gradient-warm flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isRecovery) {
    return (
      <div className="min-h-screen bg-gradient-warm flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Invalid Link</CardTitle>
            <CardDescription>
              This password reset link is invalid or has expired.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => navigate('/reset-password')}
              className="w-full"
            >
              Request New Reset Link
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-warm flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-warm border-border/50">
        <CardHeader className="space-y-2">
          <CardTitle className="text-2xl">Set New Password</CardTitle>
          <CardDescription>
            Enter your new password below
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">New Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter new password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                className="border-border/50"
              />
              <p className="text-xs text-muted-foreground">
                At least 8 characters
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={8}
                className="border-border/50"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-primary hover:bg-primary/90 shadow-soft"
              disabled={isLoading}
            >
              {isLoading ? (
                'Updating...'
              ) : (
                <>
                  <KeyRound className="w-4 h-4 mr-2" />
                  Update Password
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default UpdatePassword;
